import { useLanguage } from "../../../contexts/LanguageContext";
import { segmentLabelKey } from "../segments";
import { formatNumber, formatPercent, formatRelative } from "../format";
import { EmptyNote, Panel, Skeleton } from "./ui";

// Visit rate within 7 days of a push, against the same audience's baseline.
// Segment pushes know their exact recipients; card / all-card pushes rebuild
// the audience from who held the card at send time (approximate, A3).
export default function MessageImpact({ impact, loading }) {
  const { t, language } = useLanguage();
  const baseline = impact?.baseline_rate;
  const messages = impact?.messages || [];
  const now = new Date();
  // Bars are scaled so the best rate fills the track; the baseline tick sits
  // on the same scale.
  const scaleMax = Math.max(baseline || 0, ...messages.map((m) => m.rate || 0)) || 1;

  return (
    <Panel
      title={t("dashboard.advanced.impact.title")}
      subtitle={
        Number.isFinite(baseline)
          ? t("dashboard.advanced.impact.subtitle", { value: formatPercent(baseline, language) })
          : t("dashboard.advanced.impact.subtitleNoBaseline")
      }
      className="flex-[2_1_560px]"
    >
      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : messages.length === 0 ? (
        <EmptyNote>{t("dashboard.advanced.impact.empty")}</EmptyNote>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th scope="col" className="px-3 py-2.5 text-xs font-semibold text-gray-600">
                  {t("dashboard.advanced.impact.message")}
                </th>
                <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">
                  {t("dashboard.advanced.impact.delivered")}
                </th>
                <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">
                  {t("dashboard.advanced.impact.visited")}
                </th>
                <th scope="col" className="w-1/3 px-3 py-2.5 text-xs font-semibold text-gray-600">
                  {t("dashboard.advanced.impact.rate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => {
                const rate = Number.isFinite(m.rate)
                  ? m.rate
                  : m.delivered
                    ? (m.visited_7d || 0) / m.delivered
                    : 0;
                const above = !Number.isFinite(baseline) || rate >= baseline;
                const audience = m.segment
                  ? t(segmentLabelKey(m.segment))
                  : m.pass_name || t("marketing.filter.allPasses");
                return (
                  <tr key={m.id} className="border-t border-gray-100">
                    <td className="max-w-[16rem] px-3 py-3">
                      <span className="flex flex-col gap-0.5">
                        <span className="truncate font-medium text-gray-900" title={m.message}>
                          {m.message}
                        </span>
                        <span className="text-xs text-gray-500">
                          {[audience, m.sent_at && formatRelative(m.sent_at, language, now)]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-gray-700">
                      {formatNumber(m.delivered ?? 0, language)}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-700">
                      {formatNumber(m.visited_7d ?? 0, language)}
                    </td>
                    <td className="px-3 py-3">
                      <span className="flex items-center gap-2.5">
                        <span className="relative h-2 flex-1 rounded-full bg-gray-100" aria-hidden="true">
                          <span
                            className={`absolute inset-y-0 left-0 rounded-full ${above ? "bg-indigo-600" : "bg-orange-600"}`}
                            style={{ width: `${Math.min(100, (rate / scaleMax) * 100)}%` }}
                          />
                          {Number.isFinite(baseline) && (
                            <span
                              className="absolute -top-1 h-4 w-0.5 bg-gray-500"
                              style={{ left: `${(baseline / scaleMax) * 100}%` }}
                            />
                          )}
                        </span>
                        <span className="w-10 shrink-0 text-right font-semibold text-gray-900">
                          {formatPercent(rate, language)}
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
